import {
  clearWorldSavedGame,
  getWorldSavedGame,
  getWorldSavedGameBundle,
  getWorldSavedGameSummary,
  type WorldSavedGameBundle,
  type WorldSavedGameSummary,
} from "@dawn/adapters";
import { getSiteStrings } from "@dawn/i18n";
import {
  useEscapeToDismiss,
  useFocusOnOpen,
  useLandingModalFocusTrap,
  useLandingModalState,
} from "@site/modals/landingModalHelpers";
import { useLocale } from "@site/LocaleContext";
import { formatSavedGameSummary } from "@site/config/savedGameCopy";
import { useEffect, useRef, useState, type RefObject } from "react";
import styles from "@site/modals/LandingResumeModal.module.css";

interface LandingResumeModalProps {
  isOpen: boolean;
  onClose: () => void;
  returnFocusRef: RefObject<HTMLElement | null>;
  restoreFocus?: boolean;
  buttonsDisabled: boolean;
  onContinue: (bundle: WorldSavedGameBundle) => void | Promise<void>;
  onNewGame: () => void | Promise<void>;
}

function getLandingResumeSummary(): WorldSavedGameSummary | null {
  return getWorldSavedGameSummary();
}

function getLandingWorldResumeBundle(): WorldSavedGameBundle | null {
  return getWorldSavedGameBundle();
}

export function hasLandingResumeSave(): boolean {
  return getWorldSavedGame() !== null;
}

export function LandingResumeModal({
  isOpen,
  onClose,
  returnFocusRef,
  restoreFocus = true,
  buttonsDisabled,
  onContinue,
  onNewGame,
}: LandingResumeModalProps) {
  const { locale } = useLocale();
  const [summary, setSummary] = useState<WorldSavedGameSummary | null>(() =>
    getLandingResumeSummary(),
  );
  const continueButtonRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const { panelRef } = useLandingModalState(isOpen, returnFocusRef, restoreFocus);

  const site = getSiteStrings(locale);
  const summaryText =
    summary === null
      ? ""
      : formatSavedGameSummary(site.landing.savedGameSummary, summary);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const nextSummary = getLandingResumeSummary();
    setSummary(nextSummary);
    if (!nextSummary) {
      onCloseRef.current();
    }
  }, [isOpen]);

  useFocusOnOpen(isOpen, continueButtonRef);
  useLandingModalFocusTrap(panelRef, isOpen);
  useEscapeToDismiss(isOpen, onClose);

  const handleContinue = (): void => {
    const bundle = getLandingWorldResumeBundle();
    if (!bundle) {
      onClose();
      return;
    }

    void onContinue(bundle);
  };

  const handleNewGame = (): void => {
    clearWorldSavedGame();
    onClose();
    void onNewGame();
  };

  return (
    <div id="resume-modal" className={styles.modal} data-testid="resume-modal" hidden={!isOpen}>
      <div
        className={styles.backdrop}
        data-testid="resume-modal-backdrop"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="resume-modal-title"
      >
        <h2
          id="resume-modal-title"
          className={styles.title}
          data-testid="resume-modal-title"
        >
          {site.landing.resumeModalTitle}
        </h2>
        <p className={styles.summary} data-testid="resume-modal-summary">
          {summaryText}
        </p>
        <div className={styles.actions}>
          <button
            ref={continueButtonRef}
            className={styles.continueButton}
            type="button"
            data-testid="continue-button"
            disabled={buttonsDisabled}
            onClick={handleContinue}
          >
            {site.landing.continueLabel}
          </button>
          <button
            className={styles.newGameButton}
            type="button"
            data-testid="new-game-button"
            disabled={buttonsDisabled}
            onClick={handleNewGame}
          >
            {site.landing.newGameLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
